import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { LocationPinIcon } from './icons/NavIcons';
import { supabase } from '../config/supabase';

/**
 * Composant pour afficher un message quand aucune localisation n'est définie
 * Inclut un champ de recherche pour sélectionner une commune
 * @param {Object} props
 * @param {boolean} props.isFloating - Si true, affiche le composant en version flottante (pour la carte)
 * @param {Function} props.onSelectCommune - Callback appelé quand une commune est sélectionnée
 */
const NoLocationMessage = ({ 
  isFloating = false,
  onSelectCommune 
}) => {
  const { t, i18n } = useTranslation();
  const [searchInput, setSearchInput] = useState('');
  const [communeSuggestions, setCommuneSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchError, setSearchError] = useState('');

  const containerClass = isFloating
    ? "bg-white rounded-lg shadow-xl p-6"
    : "max-w-2xl mx-auto";

  // Recherche de communes via Supabase (même système que FilterPanel)
  useEffect(() => {
    if (!searchInput.trim() || searchInput.length < 2) {
      setCommuneSuggestions([]);
      setShowSuggestions(false);
      setSearchError('');
      return;
    }

    const timer = setTimeout(async () => {
      const input = searchInput.trim();
      
      try {
        const isPostalCode = /^\d{5}$/.test(input);
        
        if (isPostalCode) {
          const { data, error } = await supabase
            .from('communes_light')
            .select('latitude, longitude, nom_de_la_commune, code_postal, code_commune_insee')
            .eq('code_postal', input)
            .limit(50);

          if (error || !data || data.length === 0) {
            setCommuneSuggestions([]);
            setShowSuggestions(false);
            setSearchError(t('filters.noCommuneFound'));
            setTimeout(() => setSearchError(''), 3000);
            return;
          }
          
          const suggestions = data.map(commune => ({
            nom: commune.nom_de_la_commune,
            code: commune.code_commune_insee,
            codePostal: commune.code_postal,
            centre: {
              coordinates: [parseFloat(commune.longitude), parseFloat(commune.latitude)]
            },
            label: `${commune.code_postal} - ${commune.nom_de_la_commune}`
          }));
          
          setCommuneSuggestions(suggestions);
          setShowSuggestions(true);
          setSearchError('');
        } else {
          const { data, error } = await supabase
            .from('communes_light')
            .select('latitude, longitude, nom_de_la_commune, code_postal, code_commune_insee')
            .or(`nom_de_la_commune.ilike.%${input}%,code_postal.like.${input}%`)
            .limit(50);

          if (error || !data || data.length === 0) {
            setCommuneSuggestions([]);
            setShowSuggestions(false);
            setSearchError(t('filters.noCommuneFound'));
            setTimeout(() => setSearchError(''), 3000);
            return;
          }
          
          const suggestions = data.map(commune => ({
            nom: commune.nom_de_la_commune,
            code: commune.code_commune_insee,
            codePostal: commune.code_postal,
            centre: {
              coordinates: [parseFloat(commune.longitude), parseFloat(commune.latitude)]
            },
            label: `${commune.code_postal} - ${commune.nom_de_la_commune}`
          }));
          
          setCommuneSuggestions(suggestions);
          setShowSuggestions(true);
          setSearchError('');
        }
      } catch (error) {
        console.error('Erreur recherche commune:', error);
        setCommuneSuggestions([]);
        setShowSuggestions(false);
        setSearchError(t('filters.searchError'));
        setTimeout(() => setSearchError(''), 3000);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput, t]);

  const handleSelectCommune = (commune) => {
    setSearchInput(`${commune.codePostal} - ${commune.nom}`);
    setShowSuggestions(false);
    setCommuneSuggestions([]);
    setSearchError('');
    
    if (onSelectCommune) {
      onSelectCommune(commune);
    }
  };

  return (
    <div className={containerClass}>
      <div className="flex flex-col items-center justify-center py-5">
        <LocationPinIcon className="w-20 h-20 mb-6 text-bleu1" strokeWidth="2" />
      </div>
      <h2 className="text-h1 text-center mb-4">{t('location.noLocationTitle')}</h2>
      <p className="text-body text-gray-600 text-center mb-8">{t('location.noLocationMessage')}</p>
      
      {/* Champ de recherche de commune */}
      <div className="relative w-full max-w-2xl mx-auto">
        <input
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder={t('filters.locationPlaceholder')}
          className="w-full px-6 py-5 text-xl text-center border-2 border-gray-300 rounded-2xl focus:outline-none focus:ring-4 focus:ring-bleu1/50 focus:border-bleu1 transition-all shadow-md hover:shadow-lg"
          onFocus={() => {
            if (communeSuggestions.length > 0) {
              setShowSuggestions(true);
            }
          }}
        />
        
        {/* Liste des suggestions */}
        {showSuggestions && communeSuggestions.length > 0 && (
          <div className="absolute z-50 w-full mt-2 bg-white border border-gray-200 rounded-xl shadow-2xl max-h-80 overflow-y-auto">
            {communeSuggestions.map((commune, index) => (
              <button
                key={`${commune.code}-${commune.codePostal}-${index}`}
                onClick={() => handleSelectCommune(commune)}
                className="w-full px-6 py-4 text-left hover:bg-bleu1/10 transition-colors border-b border-gray-100 last:border-b-0 first:rounded-t-xl last:rounded-b-xl"
              >
                <span className="font-semibold text-lg text-bleu2">{commune.codePostal}</span>
                <span className="text-lg text-gray-600"> - {commune.nom}</span>
              </button>
            ))}
          </div>
        )}
        
        {/* Message d'erreur */}
        {searchError && (
          <p className="text-base text-red-500 mt-3 text-center font-medium">{searchError}</p>
        )}
      </div>
    </div>
  );
};

export default NoLocationMessage;
